<script setup lang="tsx">
import { Plus } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref, watch } from "vue";
import { SysOrgRoleName } from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

interface AllowedSaveToTableDto {
  guid: string;
  tableName: string;
  columnName: string;
  displayLabel?: string | null;
  description?: string | null;
  createdAt: string;
  createdByLoginGuid?: string | null;
  createdByLoginName?: string | null;
  modifiedAt?: string | null;
}

interface AvailableTableDto {
  tableName: string;
  displayName: string;
}

interface AvailableColumnDto {
  columnName: string;
  displayName: string;
  columnType: string;
}

type listItemType = AllowedSaveToTableDto;

const loading = ref(true);
const items = ref<listItemType[]>([]);
const myInfoStore = useMyInfoStore();

// Inline add state
const addLoading = ref(false);
const availableTables = ref<AvailableTableDto[]>([]);
const availableColumns = ref<AvailableColumnDto[]>([]);
const selectedTable = ref<string>("");
const selectedColumn = ref<string>("");

const hasSetupBasicRole = computed(() =>
  myInfoStore.currentSysOrg.roles.includes(SysOrgRoleName.SETUP_BASIC),
);

const refreshList = async () => {
  loading.value = true;
  try {
    const response = await apiClient.saveToTableAdmin.getApiSaveToTableAdmin(
      myInfoStore.currentSysOrgGuid,
    );
    items.value = response as listItemType[];
  } catch (error) {
    showError(error);
  } finally {
    loading.value = false;
  }
};

const loadAvailableTables = async () => {
  try {
    const response =
      await apiClient.saveToTableAdmin.getApiSaveToTableAdminAvailableTables(
        myInfoStore.currentSysOrgGuid,
      );
    availableTables.value = response as AvailableTableDto[];
  } catch (error) {
    showError(error);
    availableTables.value = [];
  }
};

onMounted(async () => {
  await Promise.all([refreshList(), loadAvailableTables()]);
});

// Fetch columns when table is selected
watch(selectedTable, async (tableName) => {
  selectedColumn.value = "";
  availableColumns.value = [];

  if (!tableName) return;

  try {
    const response =
      await apiClient.saveToTableAdmin.getApiSaveToTableAdminAvailableTablesColumns(
        myInfoStore.currentSysOrgGuid,
        tableName,
      );
    availableColumns.value = response as AvailableColumnDto[];
  } catch (error) {
    showError(error);
  }
});

// Check if the selected combination already exists
const alreadyExists = computed(() => {
  if (!selectedTable.value || !selectedColumn.value) return false;
  return items.value.some(
    (item) =>
      item.tableName === selectedTable.value &&
      item.columnName === selectedColumn.value,
  );
});

// Columns that are not yet added for the selected table
const availableColumnsFiltered = computed(() => {
  return availableColumns.value.filter(
    (col) =>
      !items.value.some(
        (item) =>
          item.tableName === selectedTable.value &&
          item.columnName === col.columnName,
      ),
  );
});

// Add new entry
const addEntry = async () => {
  if (!selectedTable.value || !selectedColumn.value) return;
  if (alreadyExists.value) return;

  addLoading.value = true;
  try {
    await apiClient.saveToTableAdmin.postApiSaveToTableAdmin(
      myInfoStore.currentSysOrgGuid,
      myInfoStore.currentSysOrgGuid,
      {
        tableName: selectedTable.value,
        columnName: selectedColumn.value,
      },
    );
    ElMessage.success(`Added ${selectedTable.value}.${selectedColumn.value}`);
    selectedColumn.value = "";
    await refreshList();
  } catch (error) {
    showError(error);
  } finally {
    addLoading.value = false;
  }
};

// Delete entry
const deleteEntry = async (item: listItemType) => {
  try {
    await ElMessageBox.confirm(
      `Remove "${item.tableName}.${item.columnName}" from the allowed list?`,
      "Confirm Delete",
      {
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        type: "warning",
      },
    );

    await apiClient.saveToTableAdmin.deleteApiSaveToTableAdmin(
      myInfoStore.currentSysOrgGuid,
      item.guid,
    );
    ElMessage.success("Removed");
    await refreshList();
  } catch (error) {
    if (error !== "cancel") {
      showError(error);
    }
  }
};

// Group items by table for display
const itemsByTable = computed(() => {
  const grouped = new Map<string, listItemType[]>();
  for (const item of items.value) {
    if (!grouped.has(item.tableName)) {
      grouped.set(item.tableName, []);
    }
    grouped.get(item.tableName)!.push(item);
  }
  // Sort columns within each table
  for (const cols of grouped.values()) {
    cols.sort((a, b) => a.columnName.localeCompare(b.columnName));
  }
  return grouped;
});
</script>

<template>
  <div class="page-container">
    <div class="page-header">
      <h2>Allowed SaveToTable Fields</h2>
      <p class="description">
        Configure which table/column combinations are available for the "Save to
        Table" special question in the form designer.
      </p>
      <el-alert type="warning">
        This is not fully integrated. Currently, the allowed fields are hard
        coded in the backend.</el-alert
      >
    </div>

    <!-- Inline Add Section -->
    <el-card class="add-section" shadow="never">
      <div class="inline-add">
        <el-select
          v-model="selectedTable"
          placeholder="Select table..."
          :disabled="!hasSetupBasicRole"
          style="width: 180px"
          clearable
        >
          <el-option
            v-for="table in availableTables"
            :key="table.tableName"
            :label="table.displayName"
            :value="table.tableName"
          />
        </el-select>

        <el-select
          v-model="selectedColumn"
          placeholder="Select column..."
          :disabled="!selectedTable || !hasSetupBasicRole"
          style="width: 220px"
          clearable
        >
          <el-option
            v-for="column in availableColumnsFiltered"
            :key="column.columnName"
            :label="`${column.displayName} (${column.columnType})`"
            :value="column.columnName"
          />
          <template #empty>
            <span class="no-columns">All columns already added</span>
          </template>
        </el-select>

        <el-button
          type="primary"
          :icon="Plus"
          :disabled="!selectedTable || !selectedColumn || !hasSetupBasicRole"
          :loading="addLoading"
          @click="addEntry"
        >
          Add
        </el-button>
      </div>
    </el-card>

    <!-- Entries List -->
    <div v-loading="loading" class="entries-container">
      <el-empty
        v-if="items.length === 0 && !loading"
        description="No fields configured yet. Select a table and column above to add one."
      />

      <div
        v-for="[tableName, columns] in itemsByTable"
        :key="tableName"
        class="table-group"
      >
        <div class="table-name">{{ tableName }}</div>
        <div class="columns-list">
          <el-tag
            v-for="item in columns"
            :key="item.guid"
            closable
            size="large"
            :disable-transitions="true"
            @close="deleteEntry(item)"
          >
            {{ item.columnName }}
          </el-tag>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  max-width: 800px;
}

.page-header {
  margin-bottom: 16px;
}

.page-header h2 {
  margin: 0 0 8px 0;
}

.page-header .description {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.add-section {
  margin-bottom: 20px;
}

.inline-add {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.entries-container {
  min-height: 200px;
}

.table-group {
  margin-bottom: 16px;
  padding: 12px 16px;
  background: var(--el-fill-color-light);
  border-radius: 8px;
}

.table-name {
  font-weight: 600;
  font-size: 14px;
  color: var(--el-text-color-primary);
  margin-bottom: 10px;
}

.columns-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.no-columns {
  color: var(--el-text-color-secondary);
  font-size: 13px;
  padding: 8px;
}
</style>
